/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
/* eslint-disable @typescript-eslint/no-var-requires */
import React from 'react';
import StoryblokClient, { ISbRichtext } from 'storyblok-js-client';
import { StoryblokService } from '../../services';
import { AnyProps } from '../types';
import { MultiLinkData, multiLink } from '../props/multi-link';

interface LinkComponentProps {
  text?: string;
  target?: string;
  icon?: string;
  link: {
    cached_url: string;
    anchor: string;
    url: string;
  };
}

interface UserTypeLinkComponentProps {
  text: string;
  link_retail: MultiLinkData;
  link_institutional: MultiLinkData;
}

interface RegexSpec {
  pattern: RegExp;
  replace: string;
}

const MarkdownSpec: RegexSpec[] = [
  {
    pattern: /<\/p><rcm-text-link/gm,
    replace: ' <rcm-text-link',
  },
  {
    pattern: /<\/rcm-text-link><p>/gm,
    replace: '</rcm-text-link> ',
  },
  {
    pattern: /<\/p><rcm-usertype-link/gm,
    replace: ' <rcm-usertype-link',
  },
  {
    pattern: /<\/rcm-usertype-link><p>/gm,
    replace: '</rcm-usertype-link> ',
  },
  {
    pattern: /<p><\/p>/g,
    replace: '',
  },
];

const rootAlias = 'home';

const componentPropRenderer = {
  'rcm-text-link': (blok: LinkComponentProps): string => {
    const { cached_url, anchor, url } = blok.link;
    const parsedLink = url || `/${cached_url.replace(rootAlias, '')}${anchor ? `#${anchor}` : ''}`.replace('//', '/');
    return `href="${parsedLink}" text="${blok.text}" target="${blok.target}" icon="${blok.icon}" link="${parsedLink}"`;
  },
  'rcm-usertype-link': (blok: UserTypeLinkComponentProps): string => {
    const { linkRetail } = multiLink('linkRetail', blok.link_retail);
    const { linkInsti } = multiLink('linkInsti', blok.link_institutional);
    return `text="${blok.text}" link-retail='${linkRetail}' link-institutional='${linkInsti}' parent-component='rcm-richtext'`;
  },
};

const pick = (attrs, allowed) => {
  if (!attrs) {
    return null;
  }
  const h = {};
  for (const key in attrs) {
    const value = attrs[key];
    if (allowed.indexOf(key) > -1 && value !== null) {
      h[key] = value;
    }
  }
  return h;
};

const isEmailLinkType = (type) => type === 'email';

const mySchema = {
  nodes: {
    horizontal_rule() {
      return {
        singleTag: 'hr',
      };
    },
    blockquote() {
      return {
        tag: 'blockquote',
      };
    },
    bullet_list() {
      return {
        tag: 'ul',
      };
    },
    code_block(node) {
      return {
        tag: [
          'pre',
          {
            tag: 'code',
            attrs: node.attrs,
          },
        ],
      };
    },
    hard_break() {
      return {
        singleTag: 'br',
      };
    },
    heading(node) {
      return {
        tag: `h${node.attrs.level}`,
      };
    },
    image(node) {
      return {
        singleTag: [
          {
            tag: 'img',
            attrs: pick(node.attrs, ['src', 'alt', 'title']),
          },
        ],
      };
    },
    list_item() {
      return {
        tag: 'li',
      };
    },
    ordered_list() {
      return {
        tag: 'ol',
      };
    },
    paragraph() {
      return {
        tag: 'p',
      };
    },
  },
  marks: {
    bold() {
      return {
        tag: 'b',
      };
    },
    strike() {
      return {
        tag: 'strike',
      };
    },
    underline() {
      return {
        tag: 'u',
      };
    },
    strong() {
      return {
        tag: 'strong',
      };
    },
    code() {
      return {
        tag: 'code',
      };
    },
    italic() {
      return {
        tag: 'i',
      };
    },
    link(node) {
      const attrs = { ...node.attrs };
      const { linktype = 'url' } = node.attrs;

      if (isEmailLinkType(linktype)) {
        attrs.href = `mailto:${attrs.href}`;
      }

      if (attrs.anchor) {
        attrs.href = `${attrs.href}#${attrs.anchor}`;
        delete attrs.anchor;
      }

      if (attrs && attrs.story) {
        attrs.href = `/${attrs?.story?.full_slug}`;
      }

      return {
        tag: [
          {
            tag: 'a',
            attrs,
          },
        ],
      };
    },
    styled(node) {
      return {
        tag: [
          {
            tag: 'span',
            attrs: node.attrs,
          },
        ],
      };
    },
  },
};

const storyblokClient = new StoryblokClient({
  accessToken: StoryblokService.getConfig().options.accessToken as string,
  richTextSchema: mySchema,
});

storyblokClient.setComponentResolver((component, blok): string => `<${component} ${componentPropRenderer[component](blok)}></${component}>`);

/**
 * Storyblok's richtext editor always forces blok level components to close the previous "tag".
 *
 * This means we need to combine the <p> tags surrounding the rcm-text-link into the same one,
 * with rcm-text-link as their child
 *
 * In order to maximize reusability of Storyblok's richTextResolver,
 * we use pattern matching and work directly on the "stringified markup" output by their tools.
 *
 * NOTE: The editor can still make links behave like their own Blok,
 * by inserting 2 carriage returns, instead of 1, after a paragraph.
 */

const forceLinksInsideParagraphs = (markupString: string): string => {
  let parsed = markupString;

  MarkdownSpec.forEach(({ pattern, replace }: RegexSpec) => {
    parsed = parsed.replace(pattern, replace);
  });

  return parsed;
};

/**
 * External Links are written by the Storyblok helper as `href="/http://…"`
 * However, they should not have the initial slash. This function removes it.
 */
const fixExternalLinks = (markupString: string): string =>
  markupString.replace(/href="\/https:\/\//gm, 'href="https://').replace(/href="\/http:\/\//gm, 'href="http://');

export const markupFromRichtextField = (storyblokHtmlSchema: ISbRichtext): string =>
  fixExternalLinks(forceLinksInsideParagraphs(storyblokClient.richTextResolver.render(storyblokHtmlSchema)));

export const RcmRichtext = ({ blok, slot }: AnyProps): JSX.Element => {
  return React.createElement('rcm-richtext', {
    // eslint-disable-next-line no-underscore-dangle
    uid: blok._uid,
    slot,
    'capitalize-first-Letter': blok.capitalize_first_letter || undefined,
    'right-to-left': blok.right_to_left || undefined,
    level: blok.level,
    width: blok.width,
    'highlight-box': blok.highlight_box,
    dangerouslySetInnerHTML: { __html: markupFromRichtextField(blok.text) },
  });
};
